import { makeAutoObservable, reaction, runInAction } from 'mobx'
import logger from '../utils/logger'
import authClient from '../auth/authClient'
import entitlementStore from './entitlementStore'
import organizationSessionStore from './organizationSessionStore'

class SessionStore {
	user = null
	isAuthChecking = true
	authStatus = 'initializing'
	bootstrapPromise = null

	constructor() {
		makeAutoObservable(this)
		reaction(
			() => this.user,
			user => {
				if (user) localStorage.setItem('user', JSON.stringify(user))
				else localStorage.removeItem('user')
			}
		)
		// The removed legacy professional profile must not survive in browser state.
		localStorage.removeItem('userInfo')
		authClient.setSessionExpiredHandler(() => this.setUnauthenticated())
	}

	bootstrap() {
		if (this.bootstrapPromise) return this.bootstrapPromise
		this.bootstrapPromise = this.bootstrapLocal()
		return this.bootstrapPromise
	}

	async bootstrapLocal() {
		const user = await authClient.bootstrap()
		if (user) {
			this.setAuthenticated(user)
			await organizationSessionStore.bootstrap()
			if (organizationSessionStore.isReady) {
				await entitlementStore.fetch({ force: true }).catch(() => null)
			}
		} else this.setUnauthenticated()
		return user
	}

	restoreSession() {
		return this.bootstrap()
	}

	setAuthenticated(user) {
		runInAction(() => {
			this.user = user
			this.authStatus = 'authenticated'
			this.isAuthChecking = false
		})
	}

	setUnauthenticated() {
		organizationSessionStore.logout()
		entitlementStore.clear()
		runInAction(() => {
			this.user = null
			this.authStatus = 'unauthenticated'
			this.isAuthChecking = false
			this.bootstrapPromise = null
		})
	}

	registerByEmail(email, password) {
		return authClient.register(email, password)
	}

	async loginByEmail(email, password) {
		logger.log('Authentication login')
		const user = await authClient.login(email, password)
		this.setAuthenticated(user)
		await organizationSessionStore.bootstrap()
		if (organizationSessionStore.isReady) {
			await entitlementStore.fetch({ force: true }).catch(() => null)
		}
		return user
	}

	async logout() {
		this.setUnauthenticated()
		await authClient.logout()
	}

	verifyEmail(token) { return authClient.verifyEmail(token) }
	resendVerification(email) { return authClient.resendVerification(email) }
	forgotPassword(email) { return authClient.forgotPassword(email) }
	resetPassword(token, password) { return authClient.resetPassword(token, password) }
}

const sessionStore = new SessionStore()
export default sessionStore
